import { FormType } from "../components/enums";
import { selectUIData as selectACUIApplicantData } from "../features/AC/Applicant/applicant.selectors";
import { setApplicantFormData } from "../features/AC/Applicant/applicant.slice";
import { selectUIData as selectACUIIncidentData } from "../features/AC/Incident/incident.selectors";
import { setIncidentFormData } from "../features/AC/Incident/incident.slice";
import { selectUIData as selectACUIPerpetratorData } from "../features/AC/Perpetrator/perpetrator.selectors";
import { setPerpetratorFormData } from "../features/AC/Perpetrator/perpetrator.slice";
import { setACSummaryFormData } from "../features/AC/Summary/summary.slice";
import { selectUIData as selectACUIVehicleData } from "../features/AC/Vehicle/vehicle.selectors";
import { setVehicleFormData } from "../features/AC/Vehicle/vehicle.slice";
import { selectUIData as selectOCPropertyUIDamagesData } from "../features/OC/Property/Damages/damages.selectors";
import { setDamagesFormData as setOCPropertyDamagesFormData } from "../features/OC/Property/Damages/damages.slice";
import { selectUIData as selectOCPropertyUIIncidentData } from "../features/OC/Property/Incident/incident.selectors";
import { setPropertyIncidentFormData as setOCPropertyIncidentFormData } from "../features/OC/Property/Incident/incident.slice";
import { selectUIData as selectOCPropertyUIOwnerData } from "../features/OC/Property/Owner/owner.selectors";
import { setOwnerFormData as setOCPropertyOwnerFormData } from "../features/OC/Property/Owner/owner.slice";
import { selectUIData as selectOCPropertyUIPerpetratorData } from "../features/OC/Property/Perpetrator/perpetrator.selectors";
import { setPerpetratorFormData as setOCPropertyPerpetratorFormData } from "../features/OC/Property/Perpetrator/perpetrator.slice";
import { setOCPropertySummaryFormData } from "../features/OC/Property/Summary/summary.slice";
import { selectUIData as selectOCVehicleDamagesUIData } from "../features/OC/Vehicle/Damages/damages.selectors";
import { setDamagesFormData as setOCVehicleDamagesFormData } from "../features/OC/Vehicle/Damages/damages.slice";
import { selectUIData as selectOCVehicleIncidentUIData } from "../features/OC/Vehicle/Incident/incident.selectors";
import { setVehicleIncidentFormData } from "../features/OC/Vehicle/Incident/incident.slice";
import { selectUIData as selectOCVehicleIntroductoryUIData } from "../features/OC/Vehicle/Introductory/introductory.selectors";
import { setVehicleIntroductoryFormData } from "../features/OC/Vehicle/Introductory/introductory.slice";
import { setOCVehicleSummaryFormData } from "../features/OC/Vehicle/Summary/summary.slice";
import { selectUIData as selectOCVehicleOwnerUIData } from "../features/OC/Vehicle/VehicleOwner/vehicleOwner.selectors";
import { setVehicleOwnerFormData } from "../features/OC/Vehicle/VehicleOwner/vehicleOwner.slice";
import { setWelcomeFormData } from "../features/Welcome/welcome.slice";
import { selectIsFormValidated } from "../store/HOC/genericUI.selectors";
import { isMainRouteWithFormValidator } from "../utilities/matchers";

import { Url } from "./enums";
import { routes } from "./routes";
import { Route, Routes } from "./types";

const generateFlattenRoutes = (
  availableRoutes: Routes = routes
): Array<string> => availableRoutes.reduce(findRouteByPath, []);

const findRouteByPath = (acc: Array<string>, currentRoute: Route) => {
  if (isMainRouteWithFormValidator(currentRoute)) {
    return [...acc, currentRoute.path];
  }

  if (currentRoute?.routes) {
    return [...acc, ...generateFlattenRoutes(currentRoute.routes)];
  }

  return acc;
};

export const flattenRoutesWithFormValidator: Array<string> = (() =>
  generateFlattenRoutes())();

export const mapUrlToFormDataSetter = {
  [Url.WELCOME as string]: setWelcomeFormData,
  [Url.AC_VEHICLE as string]: setVehicleFormData,
  [Url.AC_APPLICANT as string]: setApplicantFormData,
  [Url.AC_INCIDENT as string]: setIncidentFormData,
  [Url.AC_PERPETRATOR as string]: setPerpetratorFormData,
  [Url.AC_SUMMARY as string]: setACSummaryFormData,
  [Url.OC_VEHICLE_INTRODUCTORY as string]: setVehicleIntroductoryFormData,
  [Url.OC_VEHICLE_INCIDENT as string]: setVehicleIncidentFormData,
  [Url.OC_VEHICLE_VEHICLE_OWNER as string]: setVehicleOwnerFormData,
  [Url.OC_VEHICLE_DAMAGES as string]: setOCVehicleDamagesFormData,
  [Url.OC_VEHICLE_SUMMARY as string]: setOCVehicleSummaryFormData,
  [Url.OC_PROPERTY_PERPETRATOR as string]: setOCPropertyPerpetratorFormData,
  [Url.OC_PROPERTY_INCIDENT as string]: setOCPropertyIncidentFormData,
  [Url.OC_PROPERTY_OWNER as string]: setOCPropertyOwnerFormData,
  [Url.OC_PROPERTY_DAMAGES as string]: setOCPropertyDamagesFormData,
  [Url.OC_PROPERTY_SUMMARY as string]: setOCPropertySummaryFormData,
};

export const mapUrlToFormContextDataSelector = {
  [Url.AC_APPLICANT as string]: selectACUIApplicantData,
  [Url.AC_PERPETRATOR as string]: selectACUIPerpetratorData,
  [Url.OC_VEHICLE_INTRODUCTORY as string]: selectOCVehicleIntroductoryUIData,
  [Url.OC_VEHICLE_INCIDENT as string]: selectOCVehicleIncidentUIData,
  [Url.OC_VEHICLE_VEHICLE_OWNER as string]: selectOCVehicleOwnerUIData,
  [Url.OC_PROPERTY_INCIDENT as string]: selectOCPropertyUIIncidentData,
  [Url.OC_PROPERTY_OWNER as string]: selectOCPropertyUIOwnerData,
};

export const mapUrlToFormValidationStatusSelector = {
  [Url.AC_VEHICLE as string]: selectIsFormValidated(selectACUIVehicleData),
  [Url.AC_APPLICANT as string]: selectIsFormValidated(selectACUIApplicantData),
  [Url.AC_INCIDENT as string]: selectIsFormValidated(selectACUIIncidentData),
  [Url.AC_PERPETRATOR as string]: selectIsFormValidated(
    selectACUIPerpetratorData
  ),
  [Url.AC_SUMMARY as string]: selectIsFormValidated(selectACUIPerpetratorData),
  [Url.OC_VEHICLE_INTRODUCTORY as string]: selectIsFormValidated(
    selectOCVehicleIntroductoryUIData
  ),
  [Url.OC_VEHICLE_INCIDENT as string]: selectIsFormValidated(
    selectOCVehicleIncidentUIData
  ),
  [Url.OC_VEHICLE_VEHICLE_OWNER as string]: selectIsFormValidated(
    selectOCVehicleOwnerUIData
  ),
  [Url.OC_VEHICLE_DAMAGES as string]: selectIsFormValidated(
    selectOCVehicleDamagesUIData
  ),
  [Url.OC_VEHICLE_SUMMARY as string]: selectIsFormValidated(
    selectOCVehicleDamagesUIData
  ),
  [Url.OC_PROPERTY_PERPETRATOR as string]: selectIsFormValidated(
    selectOCPropertyUIPerpetratorData
  ),
  [Url.OC_PROPERTY_INCIDENT as string]: selectIsFormValidated(
    selectOCPropertyUIIncidentData
  ),
  [Url.OC_PROPERTY_OWNER as string]: selectIsFormValidated(
    selectOCPropertyUIOwnerData
  ),
  [Url.OC_PROPERTY_DAMAGES as string]: selectIsFormValidated(
    selectOCPropertyUIDamagesData
  ),
  [Url.OC_PROPERTY_SUMMARY as string]: selectIsFormValidated(
    selectOCPropertyUIDamagesData
  ),
};

export const mapFormTypeToSummary = {
  [FormType.WELCOME]: Url.WELCOME,
  [FormType.AC]: Url.AC_SUMMARY,
  [FormType.OC]: Url.WELCOME,
  [FormType.OC_PROPERTY]: Url.OC_PROPERTY_SUMMARY,
  [FormType.OC_PERSON]: Url.WELCOME,
  [FormType.OC_VEHICLE]: Url.OC_VEHICLE_SUMMARY,
};

//JIRA LPPLWEBMOB-721
/*
export const mapProgressRouteToSuccess: { [key: string]: string } = {
  [Url.AC_SUMMARY]: Url.AC_SENDING_SUCCESS,
  [Url.OC_VEHICLE_SUMMARY]: Url.OC_VEHICLE_SENDING_SUCCESS,
  [Url.OC_PROPERTY_SUMMARY]: Url.OC_PROPERTY_SENDING_SUCCESS,
};
*/

//JIRA LPPLWEBMOB-682
//Funkcjonalnosc wlaczona wylacznie dla sciezki AC, reszta przekierowan ustawiona "na przyszlosc" jakby funckja zglos kolejna szkode miala byc wlaczona rowniez dla OC
export const handleBackToWelcomeRedirect = {
  [FormType.WELCOME]: Url.WELCOME,
  [FormType.AC]: Url.AC_INCIDENT,
  [FormType.OC]: Url.WELCOME,
  [FormType.OC_PROPERTY]: Url.OC_PROPERTY_PERPETRATOR,
  [FormType.OC_PERSON]: Url.WELCOME,
  [FormType.OC_VEHICLE]: Url.OC_VEHICLE_INTRODUCTORY,
};

export const mapProgressRouteToSuccess: { [key: string]: string } = {
  [Url.AC_SUMMARY]: Url.AC_SENDING_SUCCESS,
  [Url.OC_VEHICLE_SUMMARY]: Url.OC_VEHICLE_SENDING_SUCCESS,
  [Url.OC_PROPERTY_SUMMARY]: Url.OC_PROPERTY_SENDING_SUCCESS,
};

export const getPreviousRoute = (currentRoute: string): string =>
  flattenRoutesWithFormValidator[
    flattenRoutesWithFormValidator.indexOf(currentRoute) - 1
  ] || "";

export const getIsPrevRoute = (
  currentPage: string,
  routePage: string
): boolean =>
  flattenRoutesWithFormValidator.indexOf(routePage) <
  flattenRoutesWithFormValidator.indexOf(currentPage);

export const getIsNextRoute = (
  currentPage: string,
  routePage: string
): boolean => {
  let currentPageIndex = flattenRoutesWithFormValidator.indexOf(currentPage);
  return flattenRoutesWithFormValidator[++currentPageIndex] === routePage;
};
